import { useState } from 'react';
import { Card, Title, Text, Flex, Badge, ProgressBar } from '@tremor/react';
import { api } from '../lib/api';
import { useConfirm } from './ConfirmDialog';
import type { K8sVllmResponse } from '../types';

interface PhiPodsPanelProps {
  data: K8sVllmResponse | null;
}

export function PhiPodsPanel({ data }: PhiPodsPanelProps) {
  const [scaling, setScaling] = useState(false);
  const [scaleError, setScaleError] = useState<string | null>(null);
  const confirm = useConfirm();

  // Loading
  if (!data) {
    return (
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Phi Pods</Title>
        <div className="mt-4 h-32 flex items-center justify-center">
          <Text className="text-gray-500">Loading...</Text>
        </div>
      </Card>
    );
  }

  // K8s not available
  if (!data.available) {
    return (
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Phi Pods</Title>
        <div className="mt-4 h-32 flex items-center justify-center">
          <Text className="text-gray-500">{data.message || 'Not available'}</Text>
        </div>
      </Card>
    );
  }

  // Error getting data
  if (data.error && !data.deployment) {
    return (
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Phi Pods</Title>
        <div className="mt-4 bg-red-950/30 border border-red-900/50 rounded p-3">
          <Text className="text-red-400 text-sm">{data.error}</Text>
        </div>
      </Card>
    );
  }

  const { deployment, pods, metricsAvailable } = data;
  const isOn = (deployment?.replicas ?? 0) > 0;
  const isReady = pods.some(p => p.ready);
  const isStartingUp = isOn && !isReady;

  const handleToggle = async () => {
    const newReplicas = isOn ? 0 : 1;
    const action = isOn ? 'Shut down' : 'Start up';

    const ok = await confirm({
      title: `${action} the GPU/vLLM pod?`,
      message: isOn
        ? 'Phi inference will stop until the pod is restarted.'
        : 'This will cost ~$2.12/hr and take 5–6 minutes to become ready.',
      confirmLabel: action,
      danger: isOn,
    });
    if (!ok) return;

    setScaling(true);
    setScaleError(null);
    try {
      await api.scaleVllm(newReplicas as 0 | 1);
    } catch (err) {
      setScaleError(err instanceof Error ? err.message : 'Failed to scale');
    } finally {
      setScaling(false);
    }
  };

  return (
    <Card className="bg-gray-900 ring-gray-800">
      <Flex justifyContent="between" alignItems="center">
        <Title className="text-gray-100">Phi Pods</Title>
        <Flex className="gap-2" justifyContent="end">
          <Badge
            color={isReady ? 'emerald' : isStartingUp ? 'amber' : 'gray'}
            size="sm"
          >
            {isReady ? 'Running' : isStartingUp ? 'Starting' : 'Off'}
          </Badge>
        </Flex>
      </Flex>

      {deployment && (
        <div className="mt-3">
          <Text className="text-gray-500 text-xs mb-1">Deployment: {deployment.name}</Text>
          {isOn && (
            <ProgressBar
              value={isReady ? 100 : 30}
              color={isReady ? 'emerald' : 'amber'}
              className="h-1"
            />
          )}
        </div>
      )}

      {/* GPU Info */}
      {isOn && (
        <div className="mt-3 pt-2 border-t border-gray-800">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-gray-800/50 rounded p-1.5 text-center">
              <Text className="text-gray-500 text-xs">GPU</Text>
              <Text className="text-gray-200 font-semibold">
                {pods[0]?.gpuLimit ? `${pods[0].gpuLimit}x L40S` : '1x GPU'}
              </Text>
            </div>
            <div className="bg-gray-800/50 rounded p-1.5 text-center">
              <Text className="text-gray-500 text-xs">Replicas</Text>
              <Text className="text-gray-200 font-semibold">
                {deployment?.readyReplicas || 0}/{deployment?.replicas || 0}
              </Text>
            </div>
          </div>
        </div>
      )}

      {/* On/Off Toggle */}
      <div className="mt-3 pt-2 border-t border-gray-800">
        <button
          onClick={handleToggle}
          disabled={scaling}
          className={`w-full py-1.5 px-3 rounded text-sm font-medium transition-colors ${
            scaling
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : isOn
                ? 'bg-red-950/50 text-red-400 hover:bg-red-950/80 border border-red-900/50'
                : 'bg-emerald-950/50 text-emerald-400 hover:bg-emerald-950/80 border border-emerald-900/50'
          }`}
        >
          {scaling ? 'Scaling...' : isOn ? 'Shut Down GPU' : 'Start GPU (~$2.12/hr)'}
        </button>
        {scaleError && (
          <Text className="text-red-400 text-xs mt-1">{scaleError}</Text>
        )}
        {isStartingUp && (
          <Text className="text-amber-400 text-xs mt-1">
            Provisioning GPU node... ~5-6 min to ready
          </Text>
        )}
      </div>

      {/* Pod list */}
      <div className="mt-4 space-y-2 max-h-[120px] overflow-y-auto">
        {pods.length === 0 ? (
          <Text className="text-gray-500 text-sm py-2">
            {isOn ? 'Pod starting...' : 'No pods (GPU off)'}
          </Text>
        ) : (
          pods.map((pod) => (
            <VllmPodRow key={pod.name} pod={pod} metricsAvailable={metricsAvailable} />
          ))
        )}
      </div>
    </Card>
  );
}

interface VllmPodRowProps {
  pod: K8sVllmResponse['pods'][0];
  metricsAvailable: boolean;
}

function VllmPodRow({ pod, metricsAvailable }: VllmPodRowProps) {
  const isHealthy = pod.ready && pod.status === 'Running';
  const hasRestarts = pod.restartCount > 0;
  const shortName = pod.name.split('-').slice(-2).join('-');

  const statusColor = isHealthy
    ? 'emerald'
    : pod.status === 'Pending'
      ? 'amber'
      : 'red';

  return (
    <div className={`p-2 rounded bg-gray-800/50 ${!isHealthy ? 'ring-1 ring-amber-900/50' : ''}`}>
      <Flex justifyContent="between" alignItems="start">
        <div className="flex-1 min-w-0">
          <Flex justifyContent="start" alignItems="center" className="gap-2">
            <Text className="text-gray-200 font-medium text-sm truncate" title={pod.name}>
              {shortName}
            </Text>
            <Badge color={statusColor} size="xs">
              {pod.status}
            </Badge>
            {hasRestarts && (
              <Badge color="amber" size="xs">
                {pod.restartCount} restarts
              </Badge>
            )}
          </Flex>
          <Text className="text-gray-500 text-xs mt-0.5">
            Age: {pod.age}
            {pod.gpuLimit && ` | GPU: ${pod.gpuLimit}`}
            {pod.nodeName && ` | ${pod.nodeName.split('.')[0]}`}
          </Text>
        </div>
      </Flex>

      {metricsAvailable && (pod.cpuPercent !== null || pod.memoryPercent !== null) && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          {pod.cpuPercent !== null && (
            <div>
              <Flex justifyContent="between" className="mb-0.5">
                <Text className="text-gray-500 text-xs">CPU</Text>
                <Text className="text-gray-400 text-xs">
                  {pod.cpuUsage} / {pod.cpuLimit}
                </Text>
              </Flex>
              <ProgressBar
                value={pod.cpuPercent}
                color={pod.cpuPercent > 80 ? 'red' : pod.cpuPercent > 60 ? 'amber' : 'emerald'}
                className="h-1"
              />
            </div>
          )}
          {pod.memoryPercent !== null && (
            <div>
              <Flex justifyContent="between" className="mb-0.5">
                <Text className="text-gray-500 text-xs">Memory</Text>
                <Text className="text-gray-400 text-xs">
                  {pod.memoryUsage} / {pod.memoryLimit}
                </Text>
              </Flex>
              <ProgressBar
                value={pod.memoryPercent}
                color={pod.memoryPercent > 80 ? 'red' : pod.memoryPercent > 60 ? 'amber' : 'emerald'}
                className="h-1"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
